// =========================================================================
// The demo's `.sectile` — the tile a section landing and a module page are
// both made of.
//
// Structure, from `renderSectionPage` (demo line 3368) and `wfTiles` (3689):
// a glyph tile in the section colour, a bold label, an optional right-aligned
// count in mono, a muted description, and a "Open ›" affordance pinned to the
// bottom so a row of tiles with descriptions of different lengths still has
// its call to action on one line.
//
// On hover the demo lifts the tile 2px, borders it in the section colour and
// **inverts the glyph** — the tile fills with the colour and the stroke goes
// white. That inversion is the whole character of the thing, so it is kept.
//
// `--sec` and its two derived tints were inline custom properties in the
// demo (`--sec-soft: <col>1A`, `--sec-line: <col>44`). Here they are
// `color-mix()` on the same colour at 10% and 27%, which is the same value
// expressed in a way that works in both themes.
//
// UI layer. Fork-owned (zone C), P5a lane.
// =========================================================================

import { cn } from '@/shared/cn';
import { SeedGlyph } from './SeedGlyph';

export interface SectionTileProps {
  /** Tile colour — the module's swatch. */
  colour: string;
  /** Inline SVG path data on a `0 0 24 24` viewBox. */
  icon: string;
  label: string;
  description: string;
  /** Right-aligned figure. The demo printed `'—'` for a count of zero. */
  count?: string;
  onSelect: () => void;
  /** Overrides "Open ›". */
  cta?: string;
  className?: string;
}

export function SectionTile({
  colour,
  icon,
  label,
  description,
  count,
  onSelect,
  cta = 'Open ›',
  className,
}: SectionTileProps) {
  const soft = `color-mix(in srgb, ${colour} 10%, transparent)`;
  const line = `color-mix(in srgb, ${colour} 27%, transparent)`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'group relative flex min-w-0 flex-col gap-[7px] overflow-hidden rounded-xl border p-4 text-left',
        'transition-[transform,box-shadow,border-color] duration-150',
        'hover:-translate-y-0.5 hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
        className,
      )}
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
      // The demo bordered the tile in its own colour on hover. Tailwind cannot
      // express a hover colour that varies per instance, and a custom property
      // would need a stylesheet rule to read it, so the two handlers set it
      // directly. Focus is covered by the ring, which does not vary.
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colour;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--hairline)';
      }}
    >
      {/* The demo's `.glow` — a blurred disc bleeding out of the top-right. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-1 top-1 h-[84px] w-[84px] rounded-full opacity-[0.07] blur-[14px]"
        style={{ background: colour }}
      />

      <span className="relative flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[9px] border transition-colors duration-150"
          style={{ background: soft, borderColor: line }}
        >
          <SeedGlyph
            markup={icon}
            className="h-[18px] w-[18px] transition-colors duration-150"
            stroke={colour}
          />
        </span>
        <span
          className="min-w-0 text-[13.5px] font-semibold leading-tight"
          style={{ color: 'var(--ink-primary)' }}
        >
          {label}
        </span>
        {count !== undefined ? (
          <span
            className="mono ml-auto shrink-0 text-[13px] font-semibold"
            style={{ color: colour }}
          >
            {count}
          </span>
        ) : null}
      </span>

      <p
        className="relative m-0 text-[11.8px] leading-[1.45]"
        style={{ color: 'var(--ink-tertiary)' }}
      >
        {description}
      </p>

      <span
        className="relative mt-auto pt-1 text-[11px] font-medium"
        style={{ color: colour }}
      >
        {cta}
      </span>
    </button>
  );
}
